import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cross, Menu, X, Phone } from "lucide-react";
import { scrollToSection, requestLeadType } from "@/lib/scroll";

const LINKS = [
  { label: "Services", href: "#services", testid: "header-nav-services" },
  { label: "Restoration", href: "#process", testid: "header-nav-restoration" },
  { label: "Our Values", href: "#values", testid: "header-nav-values" },
  { label: "Reviews", href: "#reviews", testid: "header-nav-reviews" },
  { label: "Contact", href: "#contact", testid: "header-nav-contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -90, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open
          ? "border-b border-white/10 bg-ink/75 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 md:px-10">
        <button
          data-testid="header-logo"
          onClick={() => go("#home")}
          className="group flex items-center gap-3"
        >
          <span className="flex h-9 w-9 items-center justify-center border border-gold/40 bg-gold/10 transition-colors duration-300 group-hover:bg-gold/20">
            <Cross className="h-4 w-4 text-gold" />
          </span>
          <span className="text-left leading-none">
            <span className="block font-serif text-xl font-semibold tracking-tight text-white">
              Seraph <span className="italic text-gold">Roofing</span>
            </span>
            <span className="mt-1 block text-[9px] font-bold uppercase tracking-[0.4em] text-white/45">
              Above the Rest
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="header-nav">
          {LINKS.map((link) => (
            <button
              key={link.href}
              data-testid={link.testid}
              onClick={() => go(link.href)}
              className="text-xs font-semibold uppercase tracking-[0.22em] text-white/65 transition-colors duration-300 hover:text-gold"
            >
              {link.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-6 lg:flex">
          <a
            data-testid="header-phone-link"
            href="tel:+13175169317"
            className="flex items-center gap-2 text-sm font-semibold text-white/75 transition-colors duration-300 hover:text-gold"
          >
            <Phone className="h-4 w-4 text-gold" />
            (317) 516-9317
          </a>
          <button
            data-testid="header-cta-btn"
            onClick={() => {
              requestLeadType("inspection");
              go("#contact");
            }}
            className="border border-gold/60 bg-gold/10 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink"
          >
            Free Inspection
          </button>
        </div>

        <button
          data-testid="header-menu-btn"
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center border border-white/15 text-white lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="header-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((link) => (
                <button
                  key={link.href}
                  data-testid={`${link.testid}-mobile`}
                  onClick={() => go(link.href)}
                  className="py-3 text-left font-serif text-2xl text-white/85 transition-colors duration-300 hover:text-gold"
                >
                  {link.label}
                </button>
              ))}
              <a
                data-testid="header-phone-link-mobile"
                href="tel:+13175169317"
                className="mt-4 flex items-center gap-2 text-sm font-semibold text-gold"
              >
                <Phone className="h-4 w-4" /> (317) 516-9317
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
